import { memo, useEffect, useRef, useState } from 'react';

// Mouth paths for a 100x100 viewBox face, centered around (50, 62).
const MOUTH_PATHS = {
  idle: 'M 38 62 Q 50 68 62 62',
  happy: 'M 34 58 Q 50 78 66 58 Q 50 70 34 58',
  sad: 'M 36 68 Q 50 56 64 68',
  surprised: 'M 50 60 m -6 0 a 6 6 0 1 0 12 0 a 6 6 0 1 0 -12 0',
  thinking: 'M 40 64 L 60 64',
};

const EYEBROW = {
  idle: null,
  happy: null,
  sad: 'M 30 38 Q 36 34 42 38 M 58 38 Q 64 34 70 38',
  surprised: 'M 28 34 Q 36 28 44 34 M 56 34 Q 64 28 72 34',
  thinking: 'M 56 34 Q 64 28 72 32',
};

// Blinks on a randomized interval so the character reads as alive rather than static,
// without needing any external animation library. React.memo'd per the perf notes in
// the brief - this only depends on `mood`, so it won't re-render when e.g. the message
// list updates.
function AIAvatarComponent({ mood = 'idle', size = 64 }) {
  const [isBlinking, setIsBlinking] = useState(false);
  const blinkTimeout = useRef(null);

  useEffect(() => {
    function scheduleBlink() {
      const delay = 2200 + Math.random() * 2600;
      blinkTimeout.current = setTimeout(() => {
        setIsBlinking(true);
        setTimeout(() => setIsBlinking(false), 130);
        scheduleBlink();
      }, delay);
    }
    scheduleBlink();
    return () => clearTimeout(blinkTimeout.current);
  }, []);

  const isThinking = mood === 'thinking';
  const mouthPath = MOUTH_PATHS[mood] || MOUTH_PATHS.idle;
  const eyebrowPath = EYEBROW[mood];

  return (
    <div className={`ai-avatar ai-avatar-${mood}`} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={`Zlux is ${mood}`}>
        <circle cx="50" cy="50" r="46" className="ai-avatar-face" />
        {eyebrowPath && <path d={eyebrowPath} className="ai-avatar-eyebrow" />}
        <ellipse cx="36" cy="46" rx="5.5" ry={isBlinking ? 0.6 : 5.5} className="ai-avatar-eye" />
        <ellipse cx="64" cy="46" rx="5.5" ry={isBlinking ? 0.6 : 5.5} className="ai-avatar-eye" />
        <path d={mouthPath} className="ai-avatar-mouth" fill={mood === 'happy' || mood === 'surprised' ? 'currentColor' : 'none'} />
      </svg>
      {isThinking && (
        <span className="ai-avatar-thinking-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      )}
    </div>
  );
}

export const AIAvatar = memo(AIAvatarComponent);
